import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage, FormattedDate, FormattedTime } from "react-intl";
import _ from "lodash";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { DataTable } from "../../common/DataTable";
import AuditFilters from "./AuditFilters";

import { getAction } from "./enums/actionTypes";
import { AUDITS, USERS } from "../../../utils/data/models";
import * as dataActions from "../../../utils/data/dataActions";
import { FINANCE, MANAGER } from "../../../utils/Roles";
import userHasAccess from "../../../utils/userHasAccess";

class AuditDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.getData = this.getData.bind(this);
    this.updateFilters = this.updateFilters.bind(this);

    this.state = {
      extraFilters: {},
    };
  }

  componentWillMount() {
    this.props.dataActions.clearDataList(AUDITS);
    if (userHasAccess([FINANCE, MANAGER], true)) {
      this.props.dataActions.getDataList(
        USERS,
        0,
        0,
        "",
        ["firstName:asc", "lastName:asc"],
        [
          `roles.role_type=in=(AGENT,INFORMATION_AGENT,PLAN_ADMINISTRATOR),users.user_id==${this.props.auth.user.userId}`,
          USERS.archiveFilter,
        ]
      );
    } else {
      this.props.dataActions.getDataList(
        USERS,
        0,
        0,
        "",
        ["firstName:asc", "lastName:asc"],
        [USERS.archiveFilter]
      );
    }
  }

  componentWillUnmount() {
    this.props.dataActions.clearDataList(USERS);
    this.props.dataActions.clearDataList(AUDITS);
  }

  getCols() {
    return [
      {
        name: <FormattedMessage id="audit.datatable.columns.date" />,
        key: "createdAt",
        display: (audit) => {
          return (
            <span className="highlighted-datetime">
              <span className="date">
                <FormattedDate
                  value={audit.createdAt}
                  year="numeric"
                  month="long"
                  day="2-digit"
                />
              </span>
              <span className="time">
                <FormattedTime
                  value={audit.createdAt}
                  hour="numeric"
                  minute="numeric"
                />
              </span>
            </span>
          );
        },
        widthPct: 20,
      },
      {
        name: <FormattedMessage id="audit.datatable.columns.user" />,
        display: (audit) => (
          <span>{`${audit.createdByUser.firstName} ${audit.createdByUser.lastName}`}</span>
        ),
        widthPct: 20,
      },
      {
        name: <FormattedMessage id="audit.datatable.columns.action" />,
        display: (audit) => (
          <span>
            <FormattedMessage id={getAction(audit.log.action).key} />
          </span>
        ),
        widthPct: 20,
      },
      {
        name: <FormattedMessage id="audit.datatable.columns.page" />,
        display: ({ log: { pageType, pageName, pageId } }) => {
          const tooltip = (
            <Tooltip id={pageId} className="line-break-tooltip">
              <FormattedMessage id={pageType} />
              {pageName || pageId ? " / " : ""}
              {pageName}
              {pageId ? " / " : ""}
              {pageId}
            </Tooltip>
          );
          return (
            <OverlayTrigger placement="bottom" overlay={tooltip}>
              <span>
                {pageType && (
                  <span>
                    <FormattedMessage id={pageType} />{" "}
                    {pageName || pageId ? "/" : ""}
                  </span>
                )}
                {pageName && (
                  <span>
                    {pageName} {pageId ? "/" : ""}
                  </span>
                )}
                {pageId && <span>{pageId}</span>}
              </span>
            </OverlayTrigger>
          );
        },
        widthPct: 40,
      },
    ];
  }

  getData(offset, limit, q, sortBy, filters, extraFilters = {}) {
    const additionalParams = { ...this.state.extraFilters, ...extraFilters };
    const fields = "createdByUser(firstName,lastName),log(*),createdAt,auditId";
    return this.props.dataActions.getDataList(
      AUDITS,
      offset,
      limit,
      q,
      sortBy,
      filters,
      undefined,
      undefined,
      fields,
      undefined,
      additionalParams
    );
  }

  generateFilterButtons() {
    return (
      <AuditFilters
        initialValues={{ createdByUserId: " " }}
        userList={this.props.userList}
        onFilterChange={this.updateFilters}
      />
    );
  }

  updateFilters(searchFilters) {
    const { createdByUserId, ...extraFilters } = searchFilters;

    let userIdTrimmed = createdByUserId.trim
      ? createdByUserId.trim()
      : createdByUserId;
    const filters = userIdTrimmed ? [`createdByUserId==${userIdTrimmed}`] : [];

    this.setState({ extraFilters }, () => {
      this.getData(
        this.props.offset,
        this.props.limit,
        this.props.q,
        this.props.sortBy,
        filters,
        extraFilters
      );
    });
  }

  render() {
    return (
      <DataTable
        ref="dataTable"
        getData={this.getData}
        cols={this.getCols()}
        q={this.props.q}
        filters={this.props.filters}
        offset={this.props.offset}
        data={this.props.items}
        rowsPerPage={this.props.limit}
        rowHeight={56}
        totalRows={this.props.total}
        filterButtons={this.generateFilterButtons()}
        sortBy={this.props.sortBy}
      />
    );
  }
}

AuditDataTable.propTypes = {
  auth: PropTypes.object,
  dataActions: PropTypes.object,
  filters: PropTypes.array,
  q: PropTypes.string,
  limit: PropTypes.number,
  offset: PropTypes.number,
  sortBy: PropTypes.array,
  total: PropTypes.number,
  items: PropTypes.array,
  userList: PropTypes.array,
};

const defaultPagination = {
  limit: 50,
  offset: 0,
  totalCount: 0,
};

const defaultAuditProps = {
  q: "",
  sortBy: ["createdAt:desc"],
  filters: [],
  items: null,
};

function mapStateToProps(state) {
  const { auth, data } = state;
  const audits = { ...defaultAuditProps, ...data.audits };
  const pagination = { ...defaultPagination, ...audits.pagination };

  let users = _.get(data, "users.items") || [];
  const userList = users.map((user) => ({
    value: user.userId,
    text: `${user.firstName} ${user.lastName}`,
  }));

  return {
    auth,
    filters: audits.filters,
    limit: pagination.limit,
    offset: pagination.offset,
    total: pagination.totalCount,
    q: audits.q,
    sortBy: audits.sortBy,
    items: audits.items,
    userList,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AuditDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/audit/AuditDataTable.js